import React, { useEffect, useState } from 'react';
import {
  COMMAND_STAGE_LABELS,
  canRetryCommandAttempt,
  commandChannelSummary,
  commandConnectionSummary,
  commandExecutionLabel,
  commandHistorySpacing,
  commandAttemptSummary,
  commandAttemptTone,
  commandTransportLabel,
  stageSymbol,
} from './remoteCommandFeedback';

const stageEntries = (attempt) => Object.entries(COMMAND_STAGE_LABELS).map(([key, label]) => ({
  key,
  label,
  ...(attempt?.stages?.[key] || { state: 'unknown' }),
}));

function RemoteCommandFeedback({ attempt, history = [], onRetry }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [pendingElapsedMs, setPendingElapsedMs] = useState(null);
  const tone = commandAttemptTone(attempt);
  const retryAvailable = canRetryCommandAttempt(attempt) && typeof onRetry === 'function';
  const connectionSummary = commandConnectionSummary(attempt);

  useEffect(() => {
    if (attempt?.status !== 'pending' || !attempt.startedAt) {
      setPendingElapsedMs(null);
      return undefined;
    }
    const updateElapsed = () => {
      setPendingElapsedMs(Math.max(0, Date.now() - Date.parse(attempt.startedAt)));
    };
    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 100);
    return () => window.clearInterval(intervalId);
  }, [attempt?.id, attempt?.startedAt, attempt?.status]);

  return (
    <section
      className={`phone-remote__command-feedback phone-remote__command-feedback--${tone}`}
      aria-live="polite"
      aria-label="Remote command status"
    >
      <div className="phone-remote__command-feedback-head">
        <div>
          <span>Command status</span>
          <strong>{attempt?.action || 'Ready'}</strong>
        </div>
        {attempt?.durationMs != null ? <time>{attempt.durationMs} ms</time> : null}
        {pendingElapsedMs != null ? <time>{pendingElapsedMs} ms</time> : null}
      </div>
      <div className="phone-remote__command-feedback-summary">
        {commandAttemptSummary(attempt)}
      </div>
      {connectionSummary ? (
        <div className="phone-remote__command-connection">{connectionSummary}</div>
      ) : null}

      {attempt ? (
        <div className="phone-remote__command-feedback-actions">
          <button type="button" onClick={() => setDetailsOpen((value) => !value)}>
            {detailsOpen ? 'Hide details' : 'Diagnostics'}
          </button>
          {retryAvailable ? <button type="button" onClick={onRetry}>Retry</button> : null}
        </div>
      ) : null}

      {detailsOpen && attempt ? (
        <div className="phone-remote__command-route">
          <div className="phone-remote__command-identity">
            <span>Request ID</span>
            <strong>{attempt.id}</strong>
            <span>Transport</span>
            <strong>{commandTransportLabel(attempt)}</strong>
            <span>Executed on</span>
            <strong>{commandExecutionLabel(attempt)}</strong>
          </div>
          {stageEntries(attempt).map((stage) => (
            <div key={stage.key} className={`phone-remote__command-stage phone-remote__command-stage--${stage.state}`}>
              <span>{stage.label}</span>
              <strong>{stageSymbol(stage.state)}</strong>
              <small>{stage.latencyMs != null ? `${stage.latencyMs} ms` : stage.state}</small>
              {stage.detail ? <em>{stage.detail}</em> : null}
            </div>
          ))}
          {attempt.route?.bridgeDeviceId ? (
            <div className="phone-remote__command-route-meta">
              Relay device: {attempt.route.bridgeDeviceId}
            </div>
          ) : null}
          {attempt.diagnostics ? (
            <div className="phone-remote__command-diagnostics">
              <span>Connection</span>
              <strong>{attempt.diagnostics.connectionState || 'unknown'}</strong>
              <span>Result code</span>
              <strong>{attempt.result?.code || 'unavailable'}</strong>
              <span>Relay native agent</span>
              <strong>{commandChannelSummary(attempt, 'native')}</strong>
              <span>Relay player</span>
              <strong>{commandChannelSummary(attempt, 'player')}</strong>
              <span>Target native agent</span>
              <strong>{commandChannelSummary(attempt, 'native', 'target')}</strong>
              <span>Target player</span>
              <strong>{commandChannelSummary(attempt, 'player', 'target')}</strong>
              <span>Target address</span>
              <strong>{attempt.diagnostics.targetAddress || 'unavailable'}</strong>
              <span>Route reason</span>
              <strong>{attempt.diagnostics.routeReason || 'unavailable'}</strong>
              <span>Relay attempts</span>
              <strong>
                {attempt.diagnostics.relayAttempts?.length
                  ? attempt.diagnostics.relayAttempts
                    .map((entry) => `${entry.bridgeDeviceId}:${entry.status}`)
                    .join(' → ')
                  : 'unavailable'}
              </strong>
              <span>Failed stage</span>
              <strong>{attempt.failure?.stage || 'none'}</strong>
              <span>Retry safety</span>
              <strong>{attempt.failure?.retryDisposition || 'not applicable'}</strong>
              <span>Target ready</span>
              <strong>{attempt.diagnostics.connectionReadyMs != null ? `${attempt.diagnostics.connectionReadyMs} ms` : 'unavailable'}</strong>
              <span>Command write</span>
              <strong>{attempt.diagnostics.commandDurationMs != null ? `${attempt.diagnostics.commandDurationMs} ms` : 'unavailable'}</strong>
              <span>Transport total</span>
              <strong>{attempt.diagnostics.transportDurationMs != null ? `${attempt.diagnostics.transportDurationMs} ms` : 'unavailable'}</strong>
              <span>Stale retries</span>
              <strong>{attempt.diagnostics.retryCount || 0}</strong>
            </div>
          ) : null}
          {history.length > 1 ? (
            <div className="phone-remote__command-history">
              <span>Recent commands</span>
              {history.slice(0, 6).map((entry, index) => (
                <div key={entry.id}>
                  <strong>{entry.action}</strong>
                  <small>
                    {entry.status}
                    {entry.durationMs != null ? ` · ${entry.durationMs} ms` : ''}
                    {commandHistorySpacing(history, index) != null
                      ? ` · +${commandHistorySpacing(history, index)} ms`
                      : ''}
                  </small>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export default RemoteCommandFeedback;
